import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Permission } from "../../../Permission/Dto/PermissionModel";
import { PermissionEffects } from "../../../Permission/State/PermissionEffect";
import { PermissionState } from "../../../Permission/State/PermissionState";
import { RoleEffects } from "../../State/RoleEffect";
import { RoleState } from "../../State/RoleState";
import { PermissionGroup } from "../../Dto/RolePageModel";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-role-create",
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: "./RoleCreatePage.html",
  styleUrl: "./RoleCreatePage.scss",
})
export class RoleCreatePage implements OnInit {
  private readonly router = inject(Router);
  private readonly permissionEffects = inject(PermissionEffects);
  readonly permissionState = inject(PermissionState);
  private readonly roleEffects = inject(RoleEffects);
  private readonly roleState = inject(RoleState);
  private readonly toastEffects = inject(ToastEffects);

  readonly name = new FormControl("", { nonNullable: true });
  readonly description = new FormControl("", { nonNullable: true });
  readonly permissionSearch = new FormControl("", { nonNullable: true });
  readonly selectedPermissionIds = new Set<string>();

  isSaving = false;
  errorMessage = "";

  ngOnInit(): void {
    this.permissionEffects.fetchPermissions().catch(() => undefined);
  }

  get permissionGroups(): PermissionGroup[] {
    const term = this.permissionSearch.value.trim().toLowerCase();
    const grouped = new Map<string, Permission[]>();

    for (const permission of this.permissionState.data()) {
      if (term && !this.matchesSearch(permission, term)) {
        continue;
      }

      const existing = grouped.get(permission.module) ?? [];
      existing.push(permission);
      grouped.set(permission.module, existing);
    }

    return Array.from(grouped.entries()).map(([module, permissions]) => ({
      module,
      permissions,
    }));
  }

  isPermissionSelected(permissionId: string): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  togglePermission(permissionId: string): void {
    if (this.selectedPermissionIds.has(permissionId)) {
      this.selectedPermissionIds.delete(permissionId);
    } else {
      this.selectedPermissionIds.add(permissionId);
    }
  }

  areAllModulePermissionsSelected(moduleName: string): boolean {
    const permissions = this.getModulePermissions(moduleName);
    if (permissions.length === 0) {
      return false;
    }

    return permissions.every((permission) => this.selectedPermissionIds.has(permission.id));
  }

  toggleModulePermissions(moduleName: string): void {
    const permissions = this.getModulePermissions(moduleName);
    const shouldSelectAll = !this.areAllModulePermissionsSelected(moduleName);

    for (const permission of permissions) {
      if (shouldSelectAll) {
        this.selectedPermissionIds.add(permission.id);
      } else {
        this.selectedPermissionIds.delete(permission.id);
      }
    }
  }

  selectAllPermissions(): void {
    // Get all permissions that match the current search term
    const term = this.permissionSearch.value.trim().toLowerCase();
    const allPermissions = this.permissionState.data().filter((permission) => {
      if (term) {
        return this.matchesSearch(permission, term);
      }
      return true;
    });

    // Select all permissions
    for (const permission of allPermissions) {
      this.selectedPermissionIds.add(permission.id);
    }
  }

  deselectAllPermissions(): void {
    // Get all permissions that match the current search term
    const term = this.permissionSearch.value.trim().toLowerCase();
    const allPermissions = this.permissionState.data().filter((permission) => {
      if (term) {
        return this.matchesSearch(permission, term);
      }
      return true;
    });

    // Deselect all permissions
    for (const permission of allPermissions) {
      this.selectedPermissionIds.delete(permission.id);
    }
  }

  async createRole(): Promise<void> {
    this.errorMessage = "";

    if (!this.name.value.trim()) {
      this.errorMessage = "Role name is required.";
      return;
    }

    this.isSaving = true;
    const permissionIds = Array.from(this.selectedPermissionIds);

    // Create the role with name, description, and permissions
    try {
      await this.roleEffects.createRole({
        name: this.name.value.trim(),
        description: this.description.value.trim(),
        permissionIds,
      });
      this.toastEffects.show("Role created successfully", "success");
      await this.router.navigate(["/roles"]);
    } catch (exception: unknown) {
      this.errorMessage = getBackendErrorMessage(exception, "Unable to create role.");
      this.toastEffects.show(this.errorMessage, "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/roles"]).catch(() => undefined);
  }

  private matchesSearch(permission: Permission, term: string): boolean {
    return (
      permission.module.toLowerCase().includes(term) ||
      permission.action.toLowerCase().includes(term) ||
      (permission.description ?? "").toLowerCase().includes(term)
    );
  }

  private getModulePermissions(moduleName: string): Permission[] {
    return this.permissionState.data().filter((permission) => permission.module === moduleName);
  }
}
